import { useState, useEffect, useCallback } from 'react';
import { Globe, Monitor, Smartphone, Tablet, MapPin, BarChart3, Users, Eye, TrendingUp, Clock, Layers, Radio, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FLAG_MAP = {
  US: '🇺🇸', GB: '🇬🇧', DE: '🇩🇪', FR: '🇫🇷', IN: '🇮🇳', CA: '🇨🇦', AU: '🇦🇺', NL: '🇳🇱', SE: '🇸🇪', CH: '🇨🇭',
  JP: '🇯🇵', BR: '🇧🇷', MX: '🇲🇽', ES: '🇪🇸', IT: '🇮🇹', KR: '🇰🇷', SG: '🇸🇬', AE: '🇦🇪', IL: '🇮🇱', PL: '🇵🇱',
  NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮', IE: '🇮🇪', NZ: '🇳🇿', PT: '🇵🇹', AT: '🇦🇹', BE: '🇧🇪', CZ: '🇨🇿', RO: '🇷🇴',
  ZA: '🇿🇦', PH: '🇵🇭', TH: '🇹🇭', VN: '🇻🇳', ID: '🇮🇩', MY: '🇲🇾', PK: '🇵🇰', NG: '🇳🇬', EG: '🇪🇬', SA: '🇸🇦',
  RU: '🇷🇺', CN: '🇨🇳', TR: '🇹🇷', AR: '🇦🇷', CO: '🇨🇴', CL: '🇨🇱', UA: '🇺🇦', HU: '🇭🇺', GR: '🇬🇷',
};

function fmt(n) {
  if (!n && n !== 0) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function fmtDuration(secs) {
  if (!secs || secs <= 0) return '0s';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

export default function AdminTraffic() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [geoTab, setGeoTab] = useState('country');
  const [loading, setLoading] = useState(true);

  const fetchTraffic = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/traffic?days=${days}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [days]);

  useEffect(() => { fetchTraffic(); const t = setInterval(fetchTraffic, 30000); return () => clearInterval(t); }, [fetchTraffic]);

  if (loading && !data) return <div className="text-white/40 p-8">Loading traffic data...</div>;
  if (!data) return <div className="text-white/40 p-8">No traffic data available yet. Deploy and wait for visitors.</div>;

  const totalDevices = data.devices?.reduce((s, d) => s + d.count, 0) || 1;
  const deviceColors = { desktop: '#22D3EE', mobile: '#8B5CF6', tablet: '#F59E0B', bot: '#EF4444' };
  const deviceIcons = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  return (
    <div className="space-y-6" data-testid="admin-traffic">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-space">Traffic & Visitors</h1>
          <p className="text-white/30 text-xs mt-1">Real-time visitor tracking, geo analytics, and user behavior</p>
        </div>
        <div className="flex items-center gap-3">
          {data.active_now > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
              <Radio className="w-3 h-3 text-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-bold">{data.active_now} active now</span>
            </div>
          )}
          <div className="flex gap-1 bg-white/5 rounded-lg p-1">
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${days === d ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top Metrics — 2 rows */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Requests', value: fmt(data.total_views_alltime), icon: BarChart3, color: 'text-cyan-400', sub: `${fmt(data.views_today)} today` },
          { label: 'Unique Visitors', value: fmt(data.unique_visitors), icon: Users, color: 'text-violet-400', sub: `${fmt(data.unique_today)} today` },
          { label: 'Registered Users', value: fmt(data.total_users), icon: TrendingUp, color: 'text-emerald-400', sub: `${fmt(data.users_today)} today` },
          { label: 'Active Now', value: data.active_now || 0, icon: Radio, color: 'text-green-400', sub: 'Last 5 minutes' },
          { label: 'Total Sessions', value: fmt(data.total_sessions), icon: Layers, color: 'text-amber-400', sub: `Last ${days} days` },
          { label: 'Avg Session', value: fmtDuration(data.avg_duration_sec), icon: Clock, color: 'text-pink-400', sub: 'Time on site' },
          { label: 'Pages/Session', value: data.avg_pages_per_session || 0, icon: Eye, color: 'text-blue-400', sub: 'Average depth' },
          { label: 'Countries', value: data.countries?.length || 0, icon: Globe, color: 'text-teal-400', sub: data.countries?.[0]?.name || '-' },
        ].map(m => (
          <div key={m.label} className="bg-[#0E1830] border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <m.icon className={`w-4 h-4 ${m.color}`} />
              <span className="text-[9px] text-white/25 uppercase tracking-widest">{m.label}</span>
            </div>
            <div className="text-2xl font-bold text-white font-space">{m.value}</div>
            <div className="text-[10px] text-white/25 mt-0.5">{m.sub}</div>
          </div>
        ))}
      </div>

      {/* Traffic Over Time */}
      {data.daily_traffic?.length > 0 && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white mb-1">Traffic Over Time</h2>
          <p className="text-white/25 text-xs mb-4">Daily page views and unique visitors</p>
          <div className="flex items-end gap-[2px] h-36">
            {data.daily_traffic.map((d) => {
              const maxViews = Math.max(...data.daily_traffic.map(x => x.views), 1);
              const height = Math.max((d.views / maxViews) * 100, 2);
              return (
                <div key={d.date} className="flex-1 group relative flex flex-col items-center justify-end" style={{ height: '100%' }}>
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black/90 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10 border border-white/10">
                    {d.date.slice(5)}: {d.views} views, {d.visitors} visitors
                  </div>
                  <div className="w-full rounded-t-sm bg-gradient-to-t from-cyan-500/50 to-cyan-400/70 transition-all hover:from-cyan-400 hover:to-cyan-300" style={{ height: `${height}%`, minHeight: '2px' }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-[10px] text-white/15">{data.daily_traffic[0]?.date}</span>
            <span className="text-[10px] text-white/15">{data.daily_traffic[data.daily_traffic.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Active Sessions Live */}
      {data.active_sessions?.length > 0 && (
        <div className="bg-[#0E1830] border border-emerald-500/20 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
            <h2 className="text-sm font-semibold text-white">Live Sessions</h2>
            <span className="text-xs text-emerald-400/60 ml-auto">Auto-refreshes every 30s</span>
          </div>
          <div className="space-y-2">
            {data.active_sessions.map((s, i) => (
              <div key={s.sid || i} className="flex items-center gap-3 py-2 px-3 bg-white/[0.02] rounded-lg text-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                <span className="text-white/40">{FLAG_MAP[s.country_code] || '🌐'}</span>
                <span className="text-white/60">{s.city || s.country || 'Unknown'}</span>
                <span className="text-white/20">·</span>
                <span className="text-white/40 capitalize">{s.device_type}</span>
                <span className="text-white/20">·</span>
                <span className="text-white/40">{s.browser}</span>
                <span className="ml-auto text-white/25">{s.page_count || 0} pages</span>
                <span className="text-white/15">{s.pages_visited?.slice(-1)[0] || '/'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversion Funnel */}
      {data.funnel && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-sm font-semibold text-white">Conversion Funnel</h2>
              <p className="text-white/25 text-xs mt-1">Visitor → Signup → Active User pipeline</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <span className="text-emerald-400 font-bold text-lg font-space">{data.funnel.conversion_rate}%</span>
                <span className="text-white/25 text-xs block">Signup Rate</span>
              </div>
              <div className="text-right">
                <span className="text-cyan-400 font-bold text-lg font-space">{data.funnel.activation_rate}%</span>
                <span className="text-white/25 text-xs block">Activation</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Unique Visitors', value: data.funnel.visitors, color: 'from-white/20 to-white/10', textColor: 'text-white/60' },
              { label: 'Viewed Landing Page', value: data.funnel.landing_page, color: 'from-blue-500/30 to-blue-500/15', textColor: 'text-blue-400' },
              { label: 'Viewed Pricing', value: data.funnel.pricing_page, color: 'from-violet-500/30 to-violet-500/15', textColor: 'text-violet-400' },
              { label: 'Visited Signup Page', value: data.funnel.signup_page, color: 'from-amber-500/30 to-amber-500/15', textColor: 'text-amber-400' },
              { label: 'Completed Registration', value: data.funnel.registered, color: 'from-emerald-500/30 to-emerald-500/15', textColor: 'text-emerald-400' },
              { label: 'Used Features (Active)', value: data.funnel.active_users, color: 'from-cyan-500/30 to-cyan-500/15', textColor: 'text-cyan-400' },
            ].map((step, i) => {
              const maxVal = data.funnel.visitors || 1;
              const width = Math.max((step.value / maxVal) * 100, 3);
              const prevVal = i === 0 ? step.value : [data.funnel.visitors, data.funnel.landing_page, data.funnel.pricing_page, data.funnel.signup_page, data.funnel.registered, data.funnel.active_users][i - 1];
              const dropoff = prevVal > 0 ? Math.round(((prevVal - step.value) / prevVal) * 100) : 0;
              return (
                <div key={step.label} className="flex items-center gap-3">
                  <span className="text-white/35 text-xs w-40 shrink-0 text-right">{step.label}</span>
                  <div className="flex-1 h-8 bg-white/[0.03] rounded-lg overflow-hidden relative">
                    <div className={`h-full bg-gradient-to-r ${step.color} rounded-lg flex items-center px-3 transition-all duration-700`} style={{ width: `${width}%` }}>
                      <span className={`${step.textColor} text-xs font-bold font-space`}>{step.value}</span>
                    </div>
                  </div>
                  {i > 0 && dropoff > 0 && (
                    <span className="text-red-400/50 text-[10px] w-12 shrink-0">-{dropoff}%</span>
                  )}
                </div>
              );
            })}
          </div>
          {data.funnel.demo_tries > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
              <span className="text-white/25 text-xs">Anonymous demo tries:</span>
              <span className="text-amber-400 text-xs font-bold font-space">{data.funnel.demo_tries}</span>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Country / City */}
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex bg-white/5 rounded-lg p-0.5">
              <button onClick={() => setGeoTab('country')}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${geoTab === 'country' ? 'bg-white/10 text-white' : 'text-white/40'}`}>
                Country
              </button>
              <button onClick={() => setGeoTab('city')}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${geoTab === 'city' ? 'bg-white/10 text-white' : 'text-white/40'}`}>
                City
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {(geoTab === 'country' ? data.countries : data.cities)?.slice(0, 12).map((item) => {
              const max = (geoTab === 'country' ? data.countries : data.cities)?.[0]?.count || 1;
              return (
                <div key={item.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2.5">
                      {geoTab === 'country' && <span className="text-sm">{FLAG_MAP[item.code] || '🌐'}</span>}
                      {geoTab === 'city' && <MapPin className="w-3 h-3 text-white/20" />}
                      <span className="text-white/70 text-sm">{item.name}</span>
                    </div>
                    <span className="text-white/50 text-sm font-medium font-space">{fmt(item.count)}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500/30 rounded-full" style={{ width: `${(item.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            {(geoTab === 'country' ? data.countries : data.cities)?.length === 0 && (
              <p className="text-white/20 text-sm text-center py-6">No geo data yet — deploy to start tracking</p>
            )}
          </div>
        </div>

        {/* Device */}
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white mb-4">Device</h2>
          <div className="flex rounded-full overflow-hidden h-3 mb-6">
            {data.devices?.map(d => (
              <div key={d.name} style={{ width: `${(d.count / totalDevices) * 100}%`, backgroundColor: deviceColors[d.name] || '#666' }} />
            ))}
          </div>
          <div className="space-y-3">
            {data.devices?.map(d => {
              const pct = Math.round((d.count / totalDevices) * 100);
              const Icon = deviceIcons[d.name] || Monitor;
              return (
                <div key={d.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: deviceColors[d.name] || '#666' }} />
                    <Icon className="w-4 h-4 text-white/30" />
                    <span className="text-white/70 text-sm capitalize">{d.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold text-sm">{pct}%</span>
                    <span className="text-white/30 text-xs">{fmt(d.count)} visits</span>
                  </div>
                </div>
              );
            })}
          </div>

          {data.browsers?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/5">
              <h3 className="text-white/30 text-[10px] font-semibold uppercase tracking-wider mb-3">Browsers</h3>
              <div className="space-y-2">
                {data.browsers?.slice(0, 5).map(b => (
                  <div key={b.name} className="flex items-center justify-between">
                    <span className="text-white/50 text-xs">{b.name}</span>
                    <span className="text-white/40 text-xs font-space">{fmt(b.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.os?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <h3 className="text-white/30 text-[10px] font-semibold uppercase tracking-wider mb-3">Operating Systems</h3>
              <div className="space-y-2">
                {data.os?.slice(0, 5).map(o => (
                  <div key={o.name} className="flex items-center justify-between">
                    <span className="text-white/50 text-xs">{o.name}</span>
                    <span className="text-white/40 text-xs font-space">{fmt(o.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Pages + Signups + Referrers */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white mb-4">Top Pages</h2>
          <div className="space-y-2.5">
            {data.top_pages?.slice(0, 10).map((p) => {
              const maxCount = data.top_pages?.[0]?.count || 1;
              return (
                <div key={p.path}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white/50 text-xs truncate max-w-[180px]">{p.path}</span>
                    <span className="text-white/40 text-xs font-space">{fmt(p.count)}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-violet-500/40 rounded-full" style={{ width: `${(p.count / maxCount) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white mb-1">Signups Over Time</h2>
          <p className="text-white/25 text-xs mb-4">New registrations per day</p>
          {data.daily_signups?.length > 0 ? (
            <>
              <div className="flex items-end gap-[2px] h-28">
                {data.daily_signups.map(d => {
                  const maxS = Math.max(...data.daily_signups.map(x => x.count), 1);
                  const height = Math.max((d.count / maxS) * 100, 6);
                  return (
                    <div key={d.date} className="flex-1 group relative flex flex-col items-center justify-end" style={{ height: '100%' }}>
                      <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black/90 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10 border border-white/10">
                        {d.date.slice(5)}: {d.count}
                      </div>
                      <div className="w-full rounded-t-sm bg-gradient-to-t from-emerald-500/50 to-emerald-400/70 hover:from-emerald-400 hover:to-emerald-300 transition-all" style={{ height: `${height}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-white/15">{data.daily_signups[0]?.date}</span>
                <span className="text-[10px] text-white/15">{data.daily_signups[data.daily_signups.length - 1]?.date}</span>
              </div>
              <div className="mt-3 text-center">
                <span className="text-emerald-400 font-bold font-space text-lg">{data.daily_signups.reduce((s, d) => s + d.count, 0)}</span>
                <span className="text-white/30 text-xs ml-2">total signups ({days}d)</span>
              </div>
            </>
          ) : (
            <p className="text-white/20 text-sm text-center py-8">No signups in this period</p>
          )}
        </div>

        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white mb-4">Traffic Sources</h2>
          {data.referrers?.length > 0 ? (
            <div className="space-y-3">
              {data.referrers.map(r => (
                <div key={r.source} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-3 h-3 text-white/20" />
                    <span className="text-white/60 text-xs truncate max-w-[160px]">{r.source}</span>
                  </div>
                  <span className="text-white/40 text-xs font-space">{fmt(r.count)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-white/20 text-sm">No external referrers yet</p>
              <p className="text-white/10 text-xs mt-1">Direct traffic only</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Users */}
      {data.recent_users?.length > 0 && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-white mb-4">Recent Registered Users</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/30 text-[10px] uppercase tracking-wider">
                  <th className="text-left pb-3 font-medium">Email</th>
                  <th className="text-left pb-3 font-medium">Name</th>
                  <th className="text-left pb-3 font-medium">Plan</th>
                  <th className="text-left pb-3 font-medium">Role</th>
                  <th className="text-left pb-3 font-medium">Signed Up</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_users.map((u, i) => (
                  <tr key={u.email || i} className="border-t border-white/5">
                    <td className="py-2.5 text-white/70 text-xs">{u.email}</td>
                    <td className="py-2.5 text-white/50 text-xs">{u.name || '-'}</td>
                    <td className="py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.plan === 'pro' ? 'bg-violet-500/20 text-violet-400' : 'bg-white/10 text-white/50'}`}>
                        {u.plan}
                      </span>
                    </td>
                    <td className="py-2.5 text-white/40 text-xs">{u.role}</td>
                    <td className="py-2.5 text-white/30 text-xs font-space">{u.signed_up}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
